import React, { useEffect, useRef, useState } from 'react';
import { Alert, Descriptions, Drawer, Empty, Tag, Timeline, Tooltip, Typography } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { useLiveTopic } from '../../../live/LiveContext.js';
import { applyFeesBox, feeOf } from '../../../domain/feeRate.js';
import { formatInstant, formatMoney, formatUnits } from '../../../format';
import { AwaitingRepricing, BasisTag, FeeCell, PriceCell, RequestTypeCell, ReviewsCell, StateTag } from './orderColumns.jsx';
import {
    PAYOUT_STATE_LABEL, PRICE_SIDE_LABEL, figureOf, pricingDisplay, receivedText, reviewBy, rolledTooltip, transactionTimeline
} from './orderRows.js';
import { ORDER_TYPE_COLOUR, ORDER_TYPE_LABEL } from './orders.js';
import PayoutAccounts from './PayoutAccounts.jsx';

const { Text } = Typography;

const dash = (value) => (value === null || value === undefined || value === '' ? <Text type="secondary">—</Text> : value);
const money = (value) => (value === null || value === undefined ? dash(null) : formatMoney(value) + ' LKR');

/**
 * IP-203: the leg's Apply fees choice as its full details read it - Yes, No
 * (the reviewer unticked it) or Not applicable, the last two with the reason
 * on hover (domain/feeRate.js applyFeesBox: the same words as the Review
 * dialog's checkbox).
 */
const ApplyFeesValue = ({ order }) => {
    const box = applyFeesBox(order);
    const label = !box.enabled ? 'Not applicable' : box.checked ? 'Yes' : 'No';
    return (
        <Tooltip title={box.tooltip}>
            <Tag className="order-apply-fees-tag" color={box.enabled && box.checked ? 'green' : undefined} tabIndex={0}>{label}</Tag>
        </Tooltip>
    );
};

/** A Units or Amount figure with its price basis (orderRows.js figureOf). */
const Figure = ({ order, side, format, suffix = '' }) => {
    const { value, tag, awaitingRepricing } = figureOf(order, side);
    if (awaitingRepricing) return <AwaitingRepricing />;
    if (value === null || value === undefined) return dash(null);
    return <span className="order-figure-cell">{format(value)}{suffix}<BasisTag tag={tag} /></span>;
};

/** The order's own figures and where it stands. */
function detailItems(order) {
    const rolls = reviewBy(order).rolled;
    return [
        { key: 'type', label: 'Type', children: <Tag className="order-drawer-status-tag" color={ORDER_TYPE_COLOUR[order.orderType]}>{ORDER_TYPE_LABEL[order.orderType] ?? order.orderType}</Tag> },
        { key: 'state', label: 'State', children: <StateTag order={order} /> },
        { key: 'requestType', label: 'Request type', children: <RequestTypeCell row={order} /> },
        { key: 'fund', label: 'Fund', children: order.fundId },
        { key: 'holder', label: 'Holder', children: dash(order.holderNo) },
        // IP-202: the strategy activation code, wherever a transaction is shown.
        { key: 'strategyCode', label: 'Strategy code', children: <span className="txn-id">{dash(order.strategyActivationCode)}</span> },
        { key: 'amount', label: 'Amount', children: <Figure order={order} side="amount" format={formatMoney} suffix=" LKR" /> },
        { key: 'units', label: 'Units', children: <Figure order={order} side="units" format={formatUnits} suffix={order.isFullHolding ? ' (full holding)' : ''} /> },
        { key: 'received', label: 'Received', children: formatInstant(order.receivedAt) },
        {
            key: 'dealing',
            label: 'Effective date',
            children: order.dealingDate
                ? order.dealingDate + (order.dealingDateBasis ? ' (' + order.dealingDateBasis.toLowerCase() + ')' : '')
                : dash(order.state === 'AWAITING_LIV' ? 'Set when LIV completes the instruction' : null)
        },
        // The fee and its % (a subscription's entry fee, a redemption's exit fee); nothing for a transaction with neither.
        ...(feeOf(order) || order.feeAmount ? [{ key: 'fee', label: 'Fee (LKR)', children: <FeeCell order={order} /> }] : []),
        // IP-203: whether the fund's fee applies to this leg - Yes, No (unticked at review), or Not applicable, with why.
        ...(order.fees ? [{ key: 'applyFees', label: 'Apply fees', children: <ApplyFeesValue order={order} /> }] : []),
        { key: 'reviews', label: 'Reviews', children: <ReviewsCell order={order} /> },
        ...(order.book ? [{
            key: 'book',
            label: 'Book',
            children: (
                <span className="order-drawer-book">
                    {order.book.state + ' · cut-off ' + formatInstant(order.book.cutoffAt)}
                    {order.reviewInTime === true && ' · reviewed in time'}
                    {order.reviewInTime === false && order.reviewCount > 0 && ' · reviewed after the cut-off'}
                </span>
            )
        }] : []),
        ...(rolls ? [{ key: 'rolls', label: 'Rolled', span: 2, children: rolledTooltip(rolls) }] : []),
        ...(order.money && order.orderType === 'RED' ? [{
            key: 'money',
            label: 'Payout',
            // IP-184: a redemption's payout, reconciled from the statement's debits - paid in parts when it goes to several accounts.
            children: (PAYOUT_STATE_LABEL[order.money.state] ?? order.money.state)
                + ' · ' + (receivedText(order.money, { payout: true }) ?? money(order.money.amount))
        }] : []),
        ...(order.money && order.orderType !== 'RED' ? [{
            key: 'money',
            label: 'Money',
            // IP-176: a subscription paid in parts says how much of it is in.
            children: order.money.state + (order.money.clearanceState ? ' · ' + order.money.clearanceState.toLowerCase().replace('_', ' ') : '')
                + ' · ' + (receivedText(order.money) ?? money(order.money.matchedAmount ?? order.money.amount))
        }] : []),
        ...(order.partner ? [{
            key: 'partner',
            label: 'Other leg',
            span: 2,
            children: (
                <span className="order-drawer-partner">
                    <span className="txn-id">{order.partner.transactionRef}</span>
                    <StateTag order={order.partner} />
                </span>
            )
        }] : []),
        ...(order.allocatedUnits ? [
            { key: 'allocated', label: 'Allocated', children: formatUnits(order.allocatedUnits) + ' units at ' + order.struckPrice },
            { key: 'net', label: 'Net amount', children: money(order.netAmount) }
        ] : []),
        { key: 'instruction', label: 'LIV instruction', span: 2, children: <Text code copyable className="order-drawer-instruction">{order.instructionId}</Text> }
    ];
}

/**
 * Design 8.4's Pricing: the provisional price, its basis, when the order was
 * repriced, and when its dealing date's own price was applied; whether it was
 * capped at the holding; and that it moved after its last review (IP-133:
 * information, never a gate).
 */
function pricingItems(order) {
    const pricing = order.pricing ?? {};
    const shown = pricingDisplay(order);
    return [
        { key: 'price', label: 'Unit price', children: <PriceCell order={order} /> },
        { key: 'side', label: 'Price side', children: dash(PRICE_SIDE_LABEL[pricing.priceSide] ?? pricing.priceSide ?? null) },
        { key: 'priceDate', label: 'Price date', children: dash(pricing.priceDate ?? null) },
        { key: 'repriced', label: 'Priced', children: pricing.repricedAt ? formatInstant(pricing.repricedAt) : dash(shown.price ? null : 'Not yet') },
        { key: 'completed', label: 'Effective-date price applied', children: pricing.repricingCompletedAt ? formatInstant(pricing.repricingCompletedAt) : dash('Not yet') },
        ...(pricing.capped ? [{
            key: 'capped',
            label: 'Capped',
            children: 'At the units available' + (pricing.shortfall ? ' (short by ' + pricing.shortfall + ')' : '')
        }] : []),
        ...(order.repricedSinceReview ? [{ key: 'sinceReview', label: 'Since the last review', span: 2, children: <Tag color="gold">Priced again since review</Tag> }] : [])
    ];
}

/**
 * One order's detail, apart from the Drawer so a render test can read it
 * (GET /orders/{orderId}, IP-89; design 8.4): its figures and where it
 * stands; its pricing; its review history - every review and send-back,
 * who (named), when, the comment or the reason (IP-127); its repricing
 * history, before and after (IP-113); and its state history, each person
 * named and every roll shown (IP-126), oldest first.
 *
 * @param {{ detail: { order: object, history?: object[], reviews?: object[], repricings?: object[] } }} props
 */
export const OrderDetail = ({ detail }) => {
    const order = detail.order;

    // IP-187: one transaction history - received, completed by the client, reviews, pricing,
    // reconcile, approve, units created or released, and any rejection - oldest first
    // (orderRows.js transactionTimeline). The register's other moves are not shown.
    const timeline = transactionTimeline(detail).map((e) => ({
        key: e.key,
        color: e.tone,
        title: formatInstant(e.at),
        content: (
            <>
                <Text strong>{e.title}</Text>
                {e.lines.map((line) => <div key={line} className="review-history-text">{line}</div>)}
            </>
        )
    }));

    return (
        <>
            <Descriptions size="small" column={2} className="order-drawer-desc" items={detailItems(order)} style={{ marginBottom: 20 }} />
            <h3 className="panel-title">Pricing</h3>
            <Descriptions size="small" column={2} className="order-drawer-desc" items={pricingItems(order)} style={{ marginBottom: 20 }} />
            {/* A redemption's bank accounts, each with its share of the units and the LKR paid. */}
            {detail.payoutAccounts?.length > 0 && (
                <>
                    <h3 className="panel-title order-drawer-section">Payout accounts</h3>
                    <div style={{ marginBottom: 20 }}><PayoutAccounts detail={detail} /></div>
                </>
            )}
            <h3 className="panel-title order-drawer-section">Transaction history</h3>
            {timeline.length ? <Timeline className="order-drawer-timeline" items={timeline} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No history" />}
        </>
    );
};

/**
 * One order in a drawer (GET /orders/{orderId}): read when it opens, a slower
 * answer for an order since closed never shown.
 *
 * @param {{ orderId: string|null, onClose: () => void }} props
 */
const OrderDrawer = ({ orderId, onClose }) => {
    const { api } = useAccess();
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): bumped when the
    // open order may have changed - reviewed, approved, matched, paid out - so
    // the effect below reads it again. Only while the drawer is open.
    const [version, setVersion] = useState(0);
    useLiveTopic(['orders', 'cash', 'payouts'], () => setVersion((v) => v + 1), { enabled: Boolean(orderId) });
    // The order whose detail is on screen: a live re-read of the SAME order keeps
    // showing it until the new answer arrives, instead of blanking the drawer.
    const shownFor = useRef(null);

    useEffect(() => {
        if (!orderId) {
            shownFor.current = null;
            return undefined;
        }
        let live = true;
        if (shownFor.current !== orderId) {
            // A different order: clear the last one's detail and error.
            setDetail(null);
            setError(null);
            shownFor.current = orderId;
        }
        api.orders.detail(orderId)
            .then((answer) => {
                if (!live) return;
                setDetail(answer);
                setError(null);
            })
            .catch((e) => live && setError(e.message));
        return () => {
            live = false;
        };
    }, [api, orderId, version]);

    const order = detail?.order;
    return (
        <Drawer
            open={Boolean(orderId)}
            onClose={onClose}
            size={660}
            title={order ? <span className="txn-id">{order.transactionRef}</span> : 'Transaction'}
            rootClassName="lfoc-styles-wrapper order-detail-drawer"
            destroyOnHidden
        >
            {error && <Alert type="error" showIcon title={error} className="auth-alert" />}
            {order && <OrderDetail detail={detail} />}
        </Drawer>
    );
};

export default OrderDrawer;
